import { Injectable, Inject } from '@angular/core';
import { environment } from '@env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { timeout, retry, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { Location } from '@app/shared/models/location.model';

@Injectable({
    providedIn: 'root'
})
export class LocationsService {
    private apiUrl = `${environment.apiHost}/location`;

    constructor(private http: HttpClient, @Inject(OKTA_AUTH) public oktaAuth: OktaAuth) {
    }

    async getLocationData(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/location-data?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Request for location data timed out!"))
            );
    }

    async getFiberNidImage(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/nid-image?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(20000),
                retry(3),
                catchError(error => of("Request for NID image timed out!"))
            );
    }

    async getAllLocationData() {
        return this.http.get(`${this.apiUrl}/all-location-data`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Request for all location data timed out!"))
            );
    }

    async getAllClosedLocationData() {
        return this.http.get(`${this.apiUrl}/all-location-closures`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Request for all location data timed out!"))
            );
    }

    async getAllLocations(): Promise<Location[]> {
        let locations: Location[] = [];
        try {
            const observable = await this.getAllLocationData();
            const locations_from_response = await firstValueFrom(observable);
            if (typeof locations_from_response === 'string') {
                console.log("Locations timed out!");
                return locations;
            }
            locations_from_response["message"]["locationList"].forEach(locations_json => {
                try {
                    locations.push(this.formatLocation(locations_json));
                } catch (error) {
                    console.log(`Failed to parse location ${locations_json && locations_json.locationNumber}:`, error);
                }
            });
        } catch (error) {
            console.log(error);
        }
        return locations;
    }

    async getAllClosedLocations(): Promise<Location[]> {
        let locations: Location[] = [];
        try {
            const observable = await this.getAllClosedLocationData();
            const locations_from_response = await firstValueFrom(observable);
            if (typeof locations_from_response === 'string') {
                console.log("Locations timed out!");
                return locations;
            }
            locations_from_response["message"]["locationList"].forEach(locations_json => {
                try {
                    locations.push(this.formatLocation(locations_json));
                } catch (error) {
                    console.log(`Failed to parse location ${locations_json && locations_json.locationNumber}:`, error);
                }
            });
        } catch (error) {
            console.log(error);
        }
        return locations;
    }

    formatLocation(locations_json) {
        let location: Location = new Location;
        location.name = locations_json["core"]["locationName"];
        location.number = locations_json["locationNumber"];
        location.status = locations_json["locationStatus"]["locationStatus"];
        const physicalAddress = locations_json["core"]["physicalAddress"] || {};
        const zip = physicalAddress["zipCode"] && physicalAddress["zipCode"]["zip"]
            ? physicalAddress["zipCode"]["zip"]
            : "";
        location.address =
            (physicalAddress["address1"] || "")
            + " "
            + (physicalAddress["city"] || "")
            + ", "
            + (physicalAddress["state"] || "")
            + " "
            + zip;

        if (!locations_json["core"].timeZone || !locations_json["core"]["timeZone"].timeZoneName) {
            location.timezone = "";
        } else {
            location.timezone = locations_json["core"]["timeZone"]["timeZoneName"].split(" ")[2];
        }

        // Some locations don't have operators, such as locations with a 'future' state
        location.operatorName = locations_json["operator"] && locations_json["operator"]["name"]
            ? locations_json["operator"]["name"]
            : "";

        // Some locations are closed, but only temporarily
        if (locations_json["locationStatus"]["tempCloseFlag"]) {

            location.tempCloseFlag = locations_json["locationStatus"]["tempCloseFlag"];

            // Check temp close reason
            if (locations_json["locationStatus"].hasOwnProperty("tempCloseReason")) {
                location.tempCloseReason = locations_json["locationStatus"]["tempCloseReason"]
            } else {
                location.tempCloseReason = locations_json["locationStatus"]["tempCloseReason"]
            }
        }

        // If a location is closed, then it has a closed date
        if (location.status == "closed" && locations_json["locationStatus"].hasOwnProperty("closedDate")) {
            location.closedDate = locations_json["locationStatus"]["closedDate"]
        }

        return location
    }
}